import type { ChangeEvent } from 'react';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  IconButton,
  Note,
  PlusIcon,
  SaveButton,
  Tooltip,
  TrashIcon,
  classNames,
  usePrevious,
} from '@movingimage-evp/mi-ui-component-library';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AssetUploadStatusCode,
  type GetWebcastSummaryManagerQuery,
  type LocalizedText,
  type VodChapter,
  useClearVodChaptersThumbnailsMutation,
  useDeleteVodChaptersMutation,
  useUpsertVodChaptersMutation,
} from 'src/generated/graphql-manager';
import { uploadFile } from 'src/utils';
import { mapPresentationsToSlides } from 'src/utils/map-presentations';
import { CoverVideoPlayer } from 'src/viewer/views/stream/components/cover-video-player';

import { ChapterThumbnail } from './chapter-thumbnail';
import { InputsGroup } from './inputs-group';
import { useChapterAnimations } from './use-chapter-animations';
import { useChapterValidation } from './use-chapter-validation';
import { convertToSeconds, offsetInMsToString } from '../event-summary-cue-points/format-time';
import { getNextOffset, prepareChapters } from './utils/chapter-helpers';

import styles from './event-summary-chapters.module.css';

type EventSummaryChaptersProps = {
  webcast: GetWebcastSummaryManagerQuery['webcast'];
  slideMarkersNoteVisible?: boolean;
  onSlideMarkersNoteClose?: () => void;
  onSave?: () => void;
};

export function EventSummaryChapters({
  webcast,
  slideMarkersNoteVisible,
  onSlideMarkersNoteClose,
  onSave,
}: EventSummaryChaptersProps) {
  const { t } = useTranslation();

  const [upsertChapters, { loading: upserting }] = useUpsertVodChaptersMutation();
  const [deleteChapters, { loading: deleting }] = useDeleteVodChaptersMutation();
  const [clearThumbnails, { loading: clearing }] = useClearVodChaptersThumbnailsMutation();

  const chaptersFromBackend = webcast.streaming?.vod?.chapters || [];
  const slideMarkers = webcast?.streaming?.vod?.slideMarkers || [];
  const presentations = webcast?.presentations || [];
  const languages = [webcast.primaryLang, ...(webcast.additionalLangs || [])];

  const primaryLanguageStream = webcast.streaming?.vod?.streams?.find(
    (stream) => stream.metadata?.language === webcast.primaryLang.code
  );
  const videoId = primaryLanguageStream?.reference?.videoId || '';
  const channelId = primaryLanguageStream?.reference?.channelId || '';
  const playoutUrl = `${process.env.REACT_APP_VIDEO_ENDPOINT}/${videoId}/details?channel-id=${channelId}`;

  const offsetInMs = primaryLanguageStream?.offsetInSeconds || 0;

  const videoVodManifestUrl =
    webcast.streaming?.vod?.streams?.find((stream) => stream.metadata?.language === webcast.primaryLang.code)
      ?.videoVodManifestUrl || undefined;

  const [chapters, setChapters] = useState<VodChapter[]>(prepareChapters(chaptersFromBackend, languages));
  const previousChapters = usePrevious(chapters);
  const errors = useChapterValidation(chapters);
  const { animations, onAnimationEnd } = useChapterAnimations(chapters, previousChapters);
  const [files, setFiles] = useState<Record<string, File>>({});
  const [playerPosition, setPlayerPosition] = useState<VodChapter['offsetInMs']>('00:00:00');
  const [chapterIdsToDelete, setChapterIdsToDelete] = useState<VodChapter['id'][]>([]);
  const [thumbnailChapterIdsToDelete, setThumbnailChapterIdsToDelete] = useState<VodChapter['id'][]>([]);

  const allPrimaryTitlesSet = chapters.every((chapter) =>
    chapter.titles?.some((title) => title.language === webcast.primaryLang.code && title.text.trim() !== '')
  );

  const savingDisabled = errors.length > 0 || !allPrimaryTitlesSet;

  const addChapterAtIndex = (index: number, offset?: VodChapter['offsetInMs']) => {
    setChapters((state) => [
      ...state.slice(0, index + 1),
      {
        id: `local-${Date.now()}`,
        offsetInMs: offset || getNextOffset(state[index]?.offsetInMs),
        titles: languages.map((lang) => ({ language: lang.code, text: '' })),
        thumbnail: {
          assetId: '',
          downloadLink: '',
          status: { code: AssetUploadStatusCode.PENDING, error: null },
        },
        thumbnailUploadLink: { token: '', url: '' },
      },
      ...state.slice(index + 1),
    ]);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>, chapterId: VodChapter['id']) => {
    const files = event.target.files ? Array.from(event.target.files) : [];

    if (files.length === 0) return;

    // Clear the input so that the same file can be uploaded again
    event.target.value = '';

    const file = files[0];
    const previewUrl = URL.createObjectURL(file);

    setFiles((prev) => ({ ...prev, [chapterId]: file }));

    setChapters((state) =>
      state.map((chapter) =>
        chapter.id === chapterId
          ? {
              ...chapter,
              thumbnail: {
                ...chapter.thumbnail,
                downloadLink: previewUrl,
              },
            }
          : chapter
      )
    );
  };

  const handleRemoveThumbnail = (chapterId: VodChapter['id']) => {
    if (!chapterId.startsWith('local-')) setThumbnailChapterIdsToDelete((prev) => [...prev, chapterId]);

    setChapters((state) =>
      state.map((chapter) =>
        chapter.id === chapterId ? { ...chapter, thumbnail: { ...chapter.thumbnail, downloadLink: null } } : chapter
      )
    );
  };

  const handleSetCurrentPlayerTime = (chapterId: VodChapter['id']) => {
    setChapters((state) =>
      state.map((chapter) => (chapter.id === chapterId ? { ...chapter, offsetInMs: playerPosition } : chapter))
    );
  };

  const handleOffsetChange = (chapterId: VodChapter['id'], value: string) => {
    setChapters((state) =>
      state.map((chapter) => (chapter.id === chapterId ? { ...chapter, offsetInMs: value } : chapter))
    );
  };

  const handleTitleChange = (chapterId: VodChapter['id'], value: string) => {
    setChapters((state) =>
      state.map((chapter) =>
        chapter.id === chapterId
          ? {
              ...chapter,
              titles: chapter.titles?.map((t) => (t.language === webcast.primaryLang.code ? { ...t, text: value } : t)),
            }
          : chapter
      )
    );
  };

  const handleTitlesUpdate = (chapterId: VodChapter['id'], titles: LocalizedText[]) => {
    setChapters((state) => state.map((c) => (c.id === chapterId ? { ...c, titles } : c)));
  };

  const handleDeleteChapter = (chapterId: VodChapter['id']) => {
    setChapterIdsToDelete((state) => [...state, chapterId]);

    setTimeout(() => {
      setChapters((state) => state.filter((c) => c.id !== chapterId));
    }, 200);
  };

  const handleMoveChapterUp = (chapterId: VodChapter['id']) => {
    setChapters((state) => {
      const chapterIndex = state.findIndex((chapter) => chapter.id === chapterId);

      if (chapterIndex > 0) {
        const updatedChapters = [...state];
        const [movedChapter] = updatedChapters.splice(chapterIndex, 1);
        updatedChapters.splice(chapterIndex - 1, 0, movedChapter);

        return updatedChapters;
      }

      return state;
    });
  };

  const handleMoveChapterDown = (chapterId: VodChapter['id']) => {
    setChapters((state) => {
      const chapterIndex = state.findIndex((c) => c.id === chapterId);

      if (chapterIndex < state.length - 1) {
        const updatedChapters = [...state];
        const [movedChapter] = updatedChapters.splice(chapterIndex, 1);
        updatedChapters.splice(chapterIndex + 1, 0, movedChapter);

        return updatedChapters;
      }

      return state;
    });
  };

  const saveChapters = async () => {
    const backendChaptersToDelete = chapterIdsToDelete.filter((id) => !id.startsWith('local-'));

    if (backendChaptersToDelete.length > 0) {
      await deleteChapters({
        variables: {
          input: {
            webcastId: webcast.id,
            ids: backendChaptersToDelete,
          },
        },
        onCompleted: () => setChapterIdsToDelete([]),
      });
    }

    if (thumbnailChapterIdsToDelete.length > 0) {
      await clearThumbnails({
        variables: {
          input: {
            webcastId: webcast.id,
            ids: thumbnailChapterIdsToDelete,
          },
        },
        onCompleted: () => setThumbnailChapterIdsToDelete([]),
      });
    }

    await upsertChapters({
      variables: {
        input: {
          webcastId: webcast.id,
          chapters: chapters.map((chapter) => ({
            id: chapter.id,
            offsetInMs: convertToSeconds(chapter.offsetInMs, 'hh:mm:ss').value * 1000,
            titles: chapter.titles ?? [],
          })),
        },
      },
      onCompleted: (data) => {
        if (data.upsertVodChapters.__typename !== 'UpsertVodChaptersSuccess') return;

        const chaptersWithLocalThumbnails = chapters
          .filter((chapter) => chapter.thumbnail.downloadLink?.startsWith('blob:'))
          .map((chapter) => ({
            id: chapter.id,
            thumbnailDownloadLink: chapter.thumbnail.downloadLink,
            file: files[chapter.id],
            localIndex: chapters.findIndex(({ id }) => id === chapter.id),
          }));

        const upsertedChapters = data.upsertVodChapters.chapters;

        chaptersWithLocalThumbnails.forEach(async ({ id, file, thumbnailDownloadLink, localIndex }) => {
          const chapter = id.startsWith('local-')
            ? upsertedChapters[localIndex]
            : upsertedChapters.find((ch) => ch.id === id);

          if (!chapter) return;

          if (file && thumbnailDownloadLink && chapter.thumbnailUploadLink) {
            uploadFile({ file, ...chapter.thumbnailUploadLink });

            chapter.thumbnail = {
              assetId: chapter.thumbnail?.assetId || '',
              downloadLink: thumbnailDownloadLink,
              status: { code: AssetUploadStatusCode.UPLOADED, error: null },
            };
          }
        });

        setChapters(prepareChapters(upsertedChapters, languages));
      },
    });

    onSave?.();
  };

  return (
    <>
      {slideMarkers.length > 0 && slideMarkersNoteVisible && (
        <div className={styles.noteContainer}>
          <Note variant="primary" className={styles.note} dismissLabel=" " onClose={onSlideMarkersNoteClose}>
            {t('manager.eventSummary.chapters.slideMarkersNote')}
          </Note>
        </div>
      )}

      <div className={styles.wrapper} data-testid="event-summary-chapters">
        <div className={styles.playerContainer}>
          <CoverVideoPlayer
            webcastId={webcast.id}
            videoId={videoId}
            playoutUrl={playoutUrl}
            videoVodManifestUrl={videoVodManifestUrl}
            offsetInSeconds={offsetInMs}
            language={webcast.primaryLang.code}
            chapters={chapters.map((chapter) => ({
              id: chapter.id,
              offsetInMs: convertToSeconds(chapter.offsetInMs, 'hh:mm:ss').value * 1000,
              titles: chapter.titles ?? [],
              thumbnailUrl: chapter.thumbnail?.downloadLink || undefined,
            }))}
            onPlayerTimeUpdate={(currentTime) => setPlayerPosition(offsetInMsToString(currentTime * 1000, true))}
          />
        </div>

        <div className={styles.saveButtonContainer}>
          <Tooltip
            hidden={!savingDisabled}
            label={
              !allPrimaryTitlesSet
                ? t('manager.eventSummary.chapters.allTitlesRequired')
                : t('manager.eventSummary.chapters.validationErrorsExist')
            }
          >
            <SaveButton
              data-testid="save-chapters-button"
              small
              disabled={savingDisabled}
              saving={upserting || deleting || clearing}
              saveLabel={t('manager.eventSummary.chapters.saveChapters')}
              savedLabel={t('manager.eventSummary.chapters.chaptersSaved')}
              onClick={saveChapters}
            />
          </Tooltip>
        </div>

        <div className={styles.chapterRows}>
          {chapters.map((chapter, index) => (
            <Fragment key={chapter.id}>
              <div
                data-testid={`chapter-${index + 1}`}
                className={classNames(
                  styles.chapterRow,
                  chapter.id.startsWith('local-') && styles.newChapter,
                  styles[animations.find(({ id }) => id === chapter.id)?.type ?? ''],
                  chapterIdsToDelete.includes(chapter.id) && styles.toDelete
                )}
                onAnimationEnd={(event) => onAnimationEnd(event, chapter.id)}
              >
                <span data-testid={`chapter-${index + 1}-number`} className={styles.chapterNumber}>
                  {t('manager.eventSummary.chapters.chapterNumber', { number: index + 1 })}
                </span>

                <ChapterThumbnail
                  chapter={chapter}
                  index={index}
                  onFileChange={handleFileChange}
                  onRemoveThumbnail={handleRemoveThumbnail}
                />

                <InputsGroup
                  chapter={chapter}
                  index={index}
                  slideMarkers={slideMarkers}
                  slides={mapPresentationsToSlides(presentations, webcast.primaryLang.code)}
                  primaryLangCode={webcast.primaryLang.code}
                  languages={languages}
                  errorMessage={errors.find((error) => error.id === chapter.id)?.message}
                  onOffsetChange={handleOffsetChange}
                  onSetCurrentPlayerTime={handleSetCurrentPlayerTime}
                  onTitleChange={handleTitleChange}
                  onTitlesUpdate={handleTitlesUpdate}
                />

                <IconButton
                  data-testid={`chapter-${index + 1}-delete-button`}
                  aria-label={t('manager.eventSummary.chapters.deleteChapter')}
                  naked
                  small
                  onClick={() => handleDeleteChapter(chapter.id)}
                >
                  <TrashIcon />
                </IconButton>

                {chapters.length > 1 && (
                  <div className={styles.moveButtons}>
                    <IconButton
                      data-testid={`chapter-${index + 1}-move-up-button`}
                      aria-label={t('manager.eventSummary.chapters.moveChapterUp')}
                      naked
                      small
                      disabled={index === 0}
                      onClick={() => handleMoveChapterUp(chapter.id)}
                    >
                      <ArrowUpIcon />
                    </IconButton>

                    <IconButton
                      data-testid={`chapter-${index + 1}-move-down-button`}
                      aria-label={t('manager.eventSummary.chapters.moveChapterDown')}
                      naked
                      small
                      disabled={index === chapters.length - 1}
                      onClick={() => handleMoveChapterDown(chapter.id)}
                    >
                      <ArrowDownIcon />
                    </IconButton>
                  </div>
                )}
              </div>

              {index < chapters.length - 1 && (
                <div className={styles.divider}>
                  <IconButton
                    data-testid={`chapter-${index + 1}-add-after-button`}
                    aria-label={t('manager.eventSummary.chapters.addChapter')}
                    small
                    onClick={() => addChapterAtIndex(index)}
                  >
                    <PlusIcon />
                  </IconButton>
                </div>
              )}
            </Fragment>
          ))}
        </div>

        <IconButton
          data-testid="add-chapter-button"
          aria-label={t('manager.eventSummary.chapters.addChapter')}
          naked
          onClick={() => addChapterAtIndex(chapters.length - 1, playerPosition)}
        >
          <PlusIcon /> {t('manager.eventSummary.chapters.addChapter')}
        </IconButton>
      </div>
    </>
  );
}
